import React from "react";
import { PRICING_ANALYSIS } from "../../consts/tables";
import { manageData } from "../../requests/CRUD";
import ProposalInfo from "./ProposalInfo";
import StandardText, { getDefaultConfig } from "./StandardText";
import { removeFile, uploadFile } from "../../requests/fileRequests";
import { existingFileError } from "../../consts/consts";
import filter from "../../filter";
import { executeQuery } from "../../requests/executeQuery";
import Alert from "../Alert";
import { can } from "../../access";

const STANDARD_TEXT_TAB = { name: "STANDARD TEXT", productType: "standard-text", isStandardText: true };

const PROPOSAL_TABS = [
  STANDARD_TEXT_TAB,
  { name: "RETAIL - STNL", productType: "retail-STNL", type: "STNL", standardPages: 3 },
  { name: "RETAIL - STNL - CA", productType: "retail-STNL-CA", type: "STNL", standardPages: 3 },
  { name: "RETAIL - MT", productType: "retail-MT", type: "MT", standardPages: 4 },
  { name: "RETAIL - MT - CA", productType: "retail-MT-CA", type: "MT", standardPages: 4 },
  { name: "RETAIL - MT PROFORMA", productType: "retail-MT-proforma", type: "MT", standardPages: 5 },
  { name: "OFFICE - STNL", productType: "office-STNL", type: "STNL", standardPages: 3 },
  { name: "OFFICE - MT", productType: "office-MT", type: "MT", standardPages: 4 },
  { name: "INDUSTRIAL - STNL", productType: "industrial-STNL", type: "STNL", standardPages: 3 },
  { name: "INDUSTRIAL - MT", productType: "industrial-MT", type: "MT", standardPages: 4 },
];

const TABLE_COLUMNS = ["ARCIMEDES_ROW_ID", "PRODUCT_TYPE", "ITEMS"];

function preProcess(data) {
  return data.map((val) => ({ ...val, ITEMS: JSON.parse(val.ITEMS) }));
}

function postProcess(data) {
  return data.map((val) => {
    const clean = {};
    TABLE_COLUMNS.forEach((col) => { if (val[col] !== undefined) clean[col] = val[col]; });
    clean.ITEMS = JSON.stringify(val.ITEMS);
    return clean;
  });
}

function buildDataMap(data) {
  const map = {};
  data.forEach((row, index) => {
    if (row.PRODUCT_TYPE) map[row.PRODUCT_TYPE] = { row, index };
  });
  return map;
}

let _uploadId = 0;

export default class CRUDProposal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      data: null,
      error: null,
      success: null,
      currentTab: 0,
      saving: false,
      uploads: {},
    };
    this._pendingSave = false;
    this._dataRef = null;
    this._progressTimers = {};
    this.handleChangeFile = this.handleChangeFile.bind(this);
    this.handleReorder = this.handleReorder.bind(this);
    this.handleReorderComplete = this.handleReorderComplete.bind(this);
    this.save = this.save.bind(this);
    this.handleUpload = this.handleUpload.bind(this);
  }

  componentDidMount() {
    this.fetch();
  }

  createEmptyRows() {
    return PROPOSAL_TABS.filter((t) => !t.isStandardText).map((tab) => ({
      PRODUCT_TYPE: tab.productType,
      ITEMS: [],
    }));
  }

  fetch() {
    this.setState({ data: null, error: null, success: null });
    // The server decides who sees proposals (proposals ≥ view); it never filtered by AGENT_EMAIL.
    const options = filter({}, "pricing-analysis");
    return executeQuery({ queryName: PRICING_ANALYSIS, options }).then((res) => {
      if (res && res.success && Array.isArray(res.data)) {
        let rows = res.data.length > 0 ? preProcess(res.data) : this.createEmptyRows();
        const existing = new Set(rows.map((r) => r.PRODUCT_TYPE));
        PROPOSAL_TABS.forEach((tab) => {
          if (tab.isStandardText) {
            if (!existing.has(tab.productType)) {
              rows.push({ PRODUCT_TYPE: tab.productType, ITEMS: getDefaultConfig() });
            }
          } else if (!existing.has(tab.productType)) {
            rows.push({ PRODUCT_TYPE: tab.productType, ITEMS: [] });
          }
        });
        this._dataRef = rows;
        this.setState({ data: rows, error: null });
      } else {
        this.setState({ data: false, error: res.message || "Failed to load data" });
      }
    });
  }

  /** A level that may look at the proposals but not change them (proposals = view). */
  isReadOnly() {
    return !can('proposals', 'edit');
  }

  handleChangeFile(value, index) {
    if (this.isReadOnly()) return;
    const data = [...(this._dataRef || this.state.data)];
    data[index] = { ...data[index], ITEMS: value };
    this._dataRef = data;
    this.setState({ data }, () => this.save());
  }

  handleReorder(value, index) {
    if (this.isReadOnly()) return;
    const data = [...(this._dataRef || this.state.data)];
    data[index] = { ...data[index], ITEMS: value };
    this._dataRef = data;
    this.setState({ data });
  }

  handleReorderComplete() {
    this.save();
  }

  save() {
    if (this.isReadOnly()) return;
    if (this.state.saving) {
      this._pendingSave = true;
      return;
    }
    this.setState({ saving: true, error: null, success: null });
    return manageData({
      tableCode: PRICING_ANALYSIS,
      data: postProcess(this._dataRef || this.state.data),
    }).then((res) => {
      if (res.data.responses) res = res.data.responses[0];
      if (res && res.success) {
        const newData = preProcess(res.data);
        this._dataRef = newData;
        this.setState({ saving: false, success: "Saved", data: newData }, () => {
          if (this._pendingSave) {
            this._pendingSave = false;
            this.save();
          }
        });
        setTimeout(() => this.setState({ success: null }), 2500);
      } else {
        this._pendingSave = false;
        this.setState({ saving: false, error: res && res.message ? res.message : "Save failed" });
      }
    });
  }

  handleRemoveCard(card) {
    if (this.isReadOnly()) return;
    removeFile({ fileName: card.name, FOLDER: "PRICING_ANALYSIS" });
  }

  _generateRandomString() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    return Array.from({ length: 5 }, () => chars.charAt(Math.floor(Math.random() * chars.length))).join('');
  }

  _getBase64(file) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
    });
  }

  _setUploadProgress(tabProductType, uploadId, progress) {
    this.setState((prev) => {
      const tabUploads = (prev.uploads[tabProductType] || []).map((u) =>
        u.id === uploadId ? { ...u, progress } : u
      );
      return { uploads: { ...prev.uploads, [tabProductType]: tabUploads } };
    });
  }

  _startProgressTimer(tabProductType, uploadId) {
    const start = Date.now();
    this._progressTimers[uploadId] = setInterval(() => {
      const elapsed = (Date.now() - start) / 1000;
      const simulated = Math.min(85, Math.round(10 + 75 * (1 - Math.exp(-elapsed / 4))));
      this._setUploadProgress(tabProductType, uploadId, simulated);
    }, 300);
  }

  _stopProgressTimer(uploadId) {
    if (this._progressTimers[uploadId]) {
      clearInterval(this._progressTimers[uploadId]);
      delete this._progressTimers[uploadId];
    }
  }

  _removeUpload(tabProductType, uploadId) {
    this._stopProgressTimer(uploadId);
    this.setState((prev) => {
      const tabUploads = (prev.uploads[tabProductType] || []).filter((u) => u.id !== uploadId);
      return { uploads: { ...prev.uploads, [tabProductType]: tabUploads } };
    });
  }

  handleUpload(file, tabIndex, tabProductType) {
    if (this.isReadOnly()) return;
    const id = ++_uploadId;
    const shortName = file.name.length > 20 ? file.name.slice(0, 17) + '...' : file.name;

    this.setState((prev) => {
      const tabUploads = [...(prev.uploads[tabProductType] || []), { id, progress: 3, fileName: shortName }];
      return { uploads: { ...prev.uploads, [tabProductType]: tabUploads } };
    });

    this._startProgressTimer(tabProductType, id);

    this._getBase64(file).then((base64) => {
      this._doUpload(tabProductType, tabIndex, id, { name: file.name, base64 });
    });
  }

  _doUpload(tabProductType, tabIndex, uploadId, fileData, retryName) {
    const fileName = retryName || fileData.name;

    uploadFile({ fileName, base64File: fileData.base64, FOLDER: 'PRICING_ANALYSIS' }).then((res) => {
      res = res.data.responses[0];
      if (!res || !res.success) {
        if (res.errorCode === existingFileError) {
          this._doUpload(tabProductType, tabIndex, uploadId, fileData, this._generateRandomString() + fileData.name);
        } else {
          this._removeUpload(tabProductType, uploadId);
          this.setState({ error: res && res.message ? res.message : "Upload failed" });
        }
      } else {
        this._stopProgressTimer(uploadId);
        this._setUploadProgress(tabProductType, uploadId, 100);
        const data = this._dataRef || this.state.data;
        const currentItems = (data[tabIndex] && data[tabIndex].ITEMS) || [];
        const newItems = [...currentItems, { name: fileName, link: res.data.url }];
        this.handleChangeFile(newItems, tabIndex);
        setTimeout(() => this._removeUpload(tabProductType, uploadId), 400);
      }
    });
  }

  render() {
    const { data, currentTab, error, success, uploads } = this.state;
    if (data === null) return <div className="CRUDProposal_loading">Loading...</div>;
    if (data === false) return <div className="CRUDProposal_error">{error}</div>;

    const dataMap = buildDataMap(data);

    return (
      <div className="CRUDProposal">
        <div className="CRUDProposal_header">
          <h1 className="CRUDProposal_title">Proposals</h1>
          {this.isReadOnly() && <Alert type="warn">Proposals are view-only for your access level.</Alert>}
          {success && <Alert type="success">{success}</Alert>}
          {error && <Alert type="error">{error}</Alert>}
        </div>
        <div className="CRUDProposal_tabs">
          {PROPOSAL_TABS.map((tab, i) => {
            const tabUploadCount = (uploads[tab.productType] || []).length;
            return (
              <div
                key={tab.productType}
                className={`CRUDProposal_tab${i === currentTab ? " active" : ""}`}
                onClick={() => this.setState({ currentTab: i })}
              >
                {tab.name}
                {tabUploadCount > 0 && <span className="CRUDProposal_tab_badge">{tabUploadCount}</span>}
              </div>
            );
          })}
        </div>
        <div className="CRUDProposal_content">
          {PROPOSAL_TABS.map((tab, i) => {
            if (i !== currentTab) return null;
            const entry = dataMap[tab.productType];

            if (tab.isStandardText) {
              const config = entry && entry.row.ITEMS && entry.row.ITEMS.defaults
                ? entry.row.ITEMS
                : getDefaultConfig();
              return (
                <StandardText
                  key={tab.productType}
                  config={config}
                  onSave={(newConfig) => {
                    if (entry) {
                      this.handleChangeFile(newConfig, entry.index);
                    }
                  }}
                  saving={this.state.saving}
                  readOnly={this.isReadOnly()}
                />
              );
            }

            return (
              <ProposalInfo
                key={tab.productType}
                setState={this.setState.bind(this)}
                data={entry ? entry.row.ITEMS : []}
                handleChangeFile={this.handleChangeFile}
                handleReorder={this.handleReorder}
                onReorderComplete={this.handleReorderComplete}
                onRemoveCard={(card) => this.handleRemoveCard(card)}
                onUpload={(file) => this.handleUpload(file, entry ? entry.index : -1, tab.productType)}
                uploads={uploads[tab.productType] || []}
                index={entry ? entry.index : -1}
                type={tab.type}
                standardPages={tab.standardPages}
                saving={this.state.saving}
                readOnly={this.isReadOnly()}
              />
            );
          })}
        </div>
      </div>
    );
  }
}
